import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { resultsApi } from '../services/api';
import { Link } from 'react-router-dom';

export default function ResultsPage() {
  const { user } = useAuth();
  const [posts, setPosts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const planActive = ['active', 'paused', 'canceling'].includes(user?.subscription_status ?? '');

  useEffect(() => {
    resultsApi.get().then(r => setPosts(r.data.data || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const publishedPosts = posts.filter(p => p.status === 'published');

  // Combined totals (ads + organic)
  const totalViews    = publishedPosts.reduce((s, p) => s + (p.impressions || 0), 0);
  const totalClicks   = publishedPosts.reduce((s, p) => s + (p.clicks || 0), 0);
  const totalLikes    = publishedPosts.reduce((s, p) => s + (p.organic_likes || 0), 0);
  const totalComments = publishedPosts.reduce((s, p) => s + (p.organic_comments || 0), 0);
  const hasAnyStats   = publishedPosts.some(p => p.stats_updated_at || p.organic_stats_updated_at);

  const fmt = (n: number) => n >= 1000 ? `${(n / 1000).toFixed(1)}K` : String(n);

  return (
    <div className="page">
      {/* Full-screen loading overlay */}
      {loading && (
        <div className="results-loading-overlay">
          <div className="rlo-icon">
            <span className="mi mi-24 spin" style={{ color: '#fff' }}>sync</span>
          </div>
          <div className="rlo-title">Recupero dati da Meta...</div>
          <div className="rlo-sub">Aggiornamento statistiche in corso</div>
        </div>
      )}

      <div>
        <h1>Risultati</h1>
        <p className="sub" style={{ marginTop: 4 }}>Panoramica delle promozioni</p>
      </div>

      {!planActive && (
        <div className="alert warning">
          <span className="mi mi-18">info</span>
          <span>Attiva un piano per iniziare a promuovere i tuoi contenuti.</span>
        </div>
      )}

      {planActive && (
        <>
          {/* Banner risultati totali */}
          <div className="results-banner">
            <div className="rb-label">Risultati totali</div>
            <div className="rb-value">{hasAnyStats ? fmt(totalViews) : '—'}</div>
            <div className="rb-sub">visualizzazioni totali</div>
            <div className="results-grid">
              <div className="results-cell">
                <span className="mi">visibility</span>
                <div>
                  <div className="rc-val">{hasAnyStats ? fmt(totalViews) : '—'}</div>
                  <div className="rc-lbl">Visualizzazioni</div>
                </div>
              </div>
              <div className="results-cell">
                <span className="mi">ads_click</span>
                <div>
                  <div className="rc-val">{hasAnyStats ? fmt(totalClicks) : '—'}</div>
                  <div className="rc-lbl">Click</div>
                </div>
              </div>
              <div className="results-cell">
                <span className="mi" style={{ color: '#F87171' }}>favorite</span>
                <div>
                  <div className="rc-val">{hasAnyStats ? fmt(totalLikes) : '—'}</div>
                  <div className="rc-lbl">Like</div>
                </div>
              </div>
              <div className="results-cell">
                <span className="mi" style={{ color: '#34D399' }}>chat_bubble</span>
                <div>
                  <div className="rc-val">{hasAnyStats ? fmt(totalComments) : '—'}</div>
                  <div className="rc-lbl">Commenti</div>
                </div>
              </div>
            </div>
          </div>

          {/* Per ogni post */}
          {posts.length > 0 && (
            <>
              <div className="section-header">
                <h3>Per ogni post</h3>
              </div>

              {posts.map((p: any) => {
                const isPublishing   = p.status === 'publishing';
                const isFailed       = p.status === 'failed';
                const hasPostStats = !!(p.stats_updated_at || p.organic_stats_updated_at);
                const dateStr = new Date(p.created_at).toLocaleDateString('it-IT', { day: 'numeric', month: 'long' });

                const statVal = (val: number) => {
                  if (isFailed) return '—';
                  if (isPublishing) return <span style={{ fontSize: 12 }}>In corso</span>;
                  if (!hasPostStats) return <span style={{ fontSize: 11, color: 'var(--text-3)' }}>In aggiorn.</span>;
                  return fmt(val);
                };

                return (
                  <div key={p.id} className="card" style={{ padding: 14 }}>
                    {/* Header post */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                      <div style={{ width: 44, height: 44, borderRadius: 10, background: 'linear-gradient(135deg,#1A2338,#212D42)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, overflow: 'hidden' }}>
                        {p.media_url && p.media_type !== 'video'
                          ? <img src={p.media_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                          : <span className="mi" style={{ fontSize: 22, color: 'var(--text-3)' }}>{p.media_type === 'video' ? 'videocam' : 'image'}</span>
                        }
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {p.caption?.substring(0, 60) || '(senza testo)'}
                        </div>
                        <div style={{ fontSize: 11, color: isPublishing ? 'var(--primary)' : isFailed ? 'var(--error)' : 'var(--text-3)', marginTop: 2 }}>
                          {dateStr}
                          {isPublishing && ' · In pubblicazione'}
                          {isFailed && ' · Pubblicazione fallita'}
                        </div>
                      </div>
                    </div>

                    {/* Stats unificati */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                      <div style={{ background: 'var(--s2)', borderRadius: 8, padding: '10px 12px', display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span className="mi mi-20" style={{ color: 'var(--primary)' }}>visibility</span>
                        <div>
                          <div style={{ fontSize: 17, fontWeight: 700 }}>{statVal(p.impressions || 0)}</div>
                          <div style={{ fontSize: 10, color: 'var(--text-3)' }}>Visualizzazioni</div>
                        </div>
                      </div>
                      <div style={{ background: 'var(--s2)', borderRadius: 8, padding: '10px 12px', display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span className="mi mi-20" style={{ color: '#9B5CF6' }}>ads_click</span>
                        <div>
                          <div style={{ fontSize: 17, fontWeight: 700 }}>{statVal(p.clicks || 0)}</div>
                          <div style={{ fontSize: 10, color: 'var(--text-3)' }}>Click</div>
                        </div>
                      </div>
                      <div style={{ background: 'var(--s2)', borderRadius: 8, padding: '10px 12px', display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span className="mi mi-20" style={{ color: '#F87171' }}>favorite</span>
                        <div>
                          <div style={{ fontSize: 17, fontWeight: 700 }}>{statVal(p.organic_likes || 0)}</div>
                          <div style={{ fontSize: 10, color: 'var(--text-3)' }}>Like</div>
                        </div>
                      </div>
                      <div style={{ background: 'var(--s2)', borderRadius: 8, padding: '10px 12px', display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span className="mi mi-20" style={{ color: '#34D399' }}>chat_bubble</span>
                        <div>
                          <div style={{ fontSize: 17, fontWeight: 700 }}>{statVal(p.organic_comments || 0)}</div>
                          <div style={{ fontSize: 10, color: 'var(--text-3)' }}>Commenti</div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </>
          )}

          {!loading && posts.length === 0 && (
            <div className="card" style={{ textAlign: 'center', padding: '40px 20px' }}>
              <span className="mi" style={{ fontSize: 44, color: 'var(--text-3)', display: 'block', marginBottom: 12 }}>bar_chart</span>
              <p style={{ fontWeight: 600, color: 'var(--text-2)', marginBottom: 6 }}>Nessun risultato ancora</p>
              <p className="sub" style={{ fontSize: 12, marginBottom: 20 }}>I risultati appariranno qui dopo la prima pubblicazione</p>
              <Link to="/create-post" className="btn btn-primary" style={{ display: 'inline-flex', width: 'auto', padding: '12px 24px' }}>
                <span className="mi mi-18">add</span> Pubblica ora
              </Link>
            </div>
          )}
        </>
      )}
    </div>
  );
}
